import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { NextPageContext } from 'next'
import { AppContext } from 'next/app'

import { LUMOSITY_USER_COOKIE } from '~/constants'
import createApolloClient from '~/lib/apolloClient'
import { parseCookies } from '~/utils/loginUtils'

// Based on https://hasura.io/learn/graphql/nextjs-fullstack-serverless/apollo-client/

// Pages to show the SideNav and Footer on, using AppLayout. Includes homepage.
interface NextPageContextWithApollo extends NextPageContext {
  apolloClient: ApolloClient<NormalizedCacheObject> | null
  apolloState: NormalizedCacheObject
  ctx: NextPageContextApp
}

type WithApolloFn = (client: ApolloClient<NormalizedCacheObject>) => Promise<void>
export type PagePropsReturnType = {
  apolloState: NormalizedCacheObject
  countryCode: string
}

type NextPageContextApp = NextPageContextWithApollo & AppContext
// On the client, we store the Apollo Client in the following variable.
// This prevents the client from reinitializing between page transitions.
let globalApolloClient: ApolloClient<NormalizedCacheObject> | null = null

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 */
export const initApolloClient = (
  initialState: NormalizedCacheObject,
  props?: { headers: Record<string, string> | undefined },
) => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, { headers: props?.headers })
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState)
  }

  return globalApolloClient
}

/**
 * We have to make sure there are certain common logic for both getInitialProps and getServerSideProps.
 * getServerSideProps - requires us to return the data in the format of `{ props: { ...desiredData } }`.
 * getInitialProps - requires us to return the data in the format of `{ ...desiredData } }`.
 * both needs to have same desiredData, as we will user this to prepare context like (apollo, region etc) in _app.tsx pageProps.
 * @example
 * #### getServerSideProps
 * ```ts
 *  export const getServerSideProps: GetServerSideProps<PagePropsReturnType> = async (context) => {
 *    const pagePropsData = await getPageProps({ req: context.req })
 *    return { props: { ...pagePropsData } }
 *  }
 * ```
 * #### getInitialProps
 * ```ts
 *  AccountRecovery.getInitialProps = async (ctx) => {
 *    const pagePropsData = await getPageProps({ req: ctx.req })
 *      return { ...pagePropsData }
 *  }
 * ```
 */
export async function getPageProps({
  req,
  withApolloFn,
}: {
  req: NextPageContext['req']
  withApolloFn?: WithApolloFn
}): Promise<PagePropsReturnType> {
  if (typeof window === 'undefined' && req) {
    const cookies = typeof window === 'undefined' && req ? parseCookies(req) : {}
    const token = cookies[LUMOSITY_USER_COOKIE]
    if (token) {
      req.headers = {
        ...req.headers,
        // Inject Authorization header server-side
        Authorization: `Bearer ${token}`,
      }
    }
  }

  const apolloClient = initApolloClient({}, { headers: (req?.headers as Record<string, string>) || {} })
  if (withApolloFn) {
    await withApolloFn(apolloClient)
  }

  const countryCode: string = (req?.headers?.['cloudfront-viewer-country'] as string) || ''

  return {
    apolloState: apolloClient.cache.extract(),
    countryCode,
  }
}
